// Epic 7 re-skin (ADO-903) — SiteHeader, re-laid out to the ADO-1779 Figma
// refresh in three compositions (see useHeaderLayout for the breakpoints):
//
//   desktop  ShoplinkSiteHeader 4002:15610 — blue branding bar (logo, depot tab
//            hanging off its bottom edge, account chip) — over Sub Header
//            4002:15599 — white bar with Depots / Shop / Browse, the search
//            module, Orders / Favourites / Offers and the Cart button.
//   tablet   Header / Tablet 4085:11217 — the same controls: logo, depot box and
//            account chip on the blue bar, then a darker tabs row (Depots / Shop
//            / Browse, icon cluster including Cart) and a white search row.
//   mobile   Header / Mobile 4085:11277 — logo, Depots and account icons, then
//            the depot box and a cart icon, then the search row. Shop / Browse /
//            Orders / Favourites / Offers move to a fixed bottom tab bar
//            (Figma Bottom Tab Bar 4002:17475).
//
// Themed tenants (Story 6.35 design update, ADO-1693) invert the colours via the
// `themed` prop: the branding bar becomes WHITE with the brand logo in its own
// colours, and on desktop / tablet the depot switcher moves into the brand-
// coloured nav row. On mobile it stays in the branding bar's colours, as the
// bar's second row (pinned with the sub-nav since Story 8.5 D-23), the only row
// that has room for it.
//
// Controlled + presentational. The page owns the search term + the favourites
// count. Search is submit-driven (Enter). The camera/scan control is functional
// where the owner wires it (Story 7.18, ADO-1387) and capability-gated (AC1) —
// see the `onScanClick` props below. Browse / Search / Favourites / Orders / scan
// are functional, and the Cart opens the cart side panel where the host wires it
// (Story 8.2, ADO-2011), and the cart button shows the host's `cartValue`. ADO-3749: the
// User Menu (`userMenu`) sits top right in every composition and shows who is signed in. Offers remains a
// visual PLACEHOLDER that mirrors the Figma demo values (per the placeholder-sample
// decision, not-real data). "My section" is gated OFF
// (see MY_SECTION_ENABLED) until it has a destination. Story 8.1 (ADO-2010):
// "Depots" is a real link to the choice screen where the host wires `depotsNav` (a
// placeholder otherwise), and the depot tab opens the Depot Switcher band
// (DepotSwitcherPanel) instead of a dropdown.
//
// Story 8.10 (ADO-2019) adds the "HHT Orders" link (the `hhtOrders` prop, shown only
// where the HHT workflow applies).
//
// a11y (NFR24-26): native button/link controls with labels/aria-labels and 44px
// hit areas; the favourites count is in the link's accessible name. Controls
// that change place between compositions are rendered once, in the place the
// current composition draws them, so there is never a CSS-hidden twin in the
// accessibility tree.
//
// Scroll-minimize (Epic 7 pixel parity, ADO-903): the blue branding bar scrolls
// away with the page; the sub-nav ALONE pins to the viewport top the instant its
// top edge reaches 0 (pure CSS sticky — the <header> is `display: contents` so
// the sub-nav sticks against the page scroll, not the header's own box). On
// tablet the sub-nav is the tabs row + search row, on mobile the depot row (the
// depot box and the cart, drawn as the branding bar's second row, Story 8.5
// D-23) + search row.
// While actually stuck the bar carries `data-stuck`, and the header writes the
// measured `--shoplink-header-offset` (full header unscrolled ↔ sub-nav-only
// pinned) so the filters rail tracks the pinned edge live.

import {
  type FocusEvent,
  type FormEvent,
  type ReactNode,
  type Ref,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { Link, NavLink } from 'react-router';
import { MAX_SEARCH_QUERY_LENGTH } from '../../api/search.client';
import { linesLabel, UNKNOWN_TOTAL } from '../../basket/cartTotals';
import { formatMoney, type MoneyValue } from '../../shared/money';
import { isCutOffShown } from '../../shared/cutOffDisplay';
import {
  CutOffAnnouncer,
  CutOffPill,
  type CutOffPillCountdown,
  type CutOffPillSize,
  type CutOffPillStatus,
} from '../CutOffPill/CutOffPill';
import { Collapse } from '../Collapse/Collapse';
import { useExitTransition } from '../hooks/useExitTransition';
import { Icon } from '../Icon/Icon';
import { HhtOrdersLink } from './HhtOrdersLink';
import { RbpShoppingListLink } from './RbpShoppingListLink';
import { MOTION_MS } from '../motion';
import { DepotSwitcherPanel, type SiteHeaderDepotOption } from './DepotSwitcherPanel';
import { UserMenu, type UserMenuAccount, type UserMenuUser } from '../UserMenu/UserMenu';
import styles from './SiteHeader.module.css';

export type { SiteHeaderDepotOption } from './DepotSwitcherPanel';

/** ADO-3749: what the header's User Menu shows (see `UserMenu`). */
export interface SiteHeaderUserMenu {
  /** The signed-in person; null until the names are known. */
  readonly user: UserMenuUser | null;
  /** The active account; null with none. */
  readonly account: UserMenuAccount | null;
  readonly onSignOut?: (() => void) | undefined;
}
import { useHeaderLayout } from './useHeaderLayout';

// Placeholder sample values — mirror the Figma demo chrome (ADO-1779) so the
// header reads as complete. Wired to real sources in later phases.
const DEPOT_NAME = 'VC Dublin';
const OFFERS_COUNT = 3;

// Manager-dashboard entry (Story 6.31, ADO-1253) is on HOLD: the placement in the
// header is undecided, so the link is hidden for now while its prop plumbing
// (showManagerDashboard, threaded from every admin-capable caller) stays intact.
// Flip this to `true` to bring the link back with no other change.
const MANAGER_DASHBOARD_ENABLED = false;
// "My section" is a coming-soon placeholder with no destination yet, so its
// branding-bar control is hidden for now. The markup stays intact; flip this to
// `true` to bring the control back with no other change.
const MY_SECTION_ENABLED = false;
const CART_COUNT = '16';
/** The largest line count the cart badge spells out; above it the badge reads "99+". */
const CART_BADGE_MAX = 99;

// Grace period before a hover-out closes the browse mega-menu, so a brief pointer
// excursion (skimming the edge of the bar on the way to the menu) does not slam it
// shut. Returning the pointer inside this window cancels the close.
const BROWSE_HOVER_CLOSE_GRACE_MS = 300;

export interface SiteHeaderProps {
  /** Controlled value of the header search box. */
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  onClearSearch?: () => void;
  /** Logo / "Shop" return to the catalogue home (clears an active search). */
  onNavigateHome?: () => void;
  /**
   * Brand logo (Story 6.35 / ADO-1693, white-label theming). Defaults to the
   * ShopLink logo when absent, so callers that pass no brand render unchanged.
   */
  logo?: { readonly src: string; readonly alt: string };
  /**
   * Themed tenant (Story 6.35 design update, ADO-1693). When true the header
   * splits into a WHITE top bar (brand logo + account badge) over the BRAND-
   * coloured nav bar (depot / Shop / Browse, search, Offers/Favourites/Orders,
   * cart) — the logo is lifted out of the coloured bar. When false (the default
   * ShopLink brand, or no brand config) the header renders unchanged: the logo
   * stays in the blue branding bar and there is no white bar.
   */
  themed?: boolean;
  /** Live favourites count for the header badge (0 hides the badge). */
  favouritesCount?: number;
  onBrowseClick?: () => void;
  /** When a boolean, Browse is a disclosure trigger (advertises aria-haspopup/
   *  expanded); undefined = plain button (this slice — the mega-menu is a later
   *  phase). */
  browseExpanded?: boolean;
  /** Id of the browse mega-menu container (the `children` overlay), for the Browse
   *  trigger's `aria-controls` when it's a disclosure. */
  browseMenuId?: string;
  /** Dismisses the browse mega-menu on outside click / hover-out; only meaningful
   *  when Browse is a disclosure (`browseExpanded` provided). */
  onBrowseDismiss?: () => void;
  /**
   * Depot selector (Story 7-FU-F). When `depots` has entries and `onSelectDepot`
   * is provided, the depot tab is a functional dropdown that switches the active
   * depot; otherwise it renders as a static placeholder tab. `depotName` is the
   * active depot's name; `depotCutOff` its cut-off (Story 8.5).
   */
  depots?: readonly SiteHeaderDepotOption[];
  activeDepotId?: string | null;
  onSelectDepot?: (id: string) => void;
  /**
   * Story 8.1 (ADO-2010), A9.1 — the active account line under the depot name
   * ("Main grocery · 3205484"), shown when the active entry names its account (Story 8.4,
   * ADO-2013: every Value Centre entry with an account, or any entry offering several).
   */
  depotDetail?: string | undefined;
  /**
   * Story 8.1 (ADO-2010), A9.1 / A9.3 — the "Depots" nav entry as a real link (to the
   * choice screen), or "Home" (to the landing page) for a single-account Customer.
   * `null` hides it (a single-account Customer with no landing page has nowhere to go).
   * Omitted, it stays the visual placeholder.
   */
  depotsNav?: { readonly label: 'Depots' | 'Home'; readonly to: string } | null | undefined;
  /**
   * Story 8.1 (ADO-2010), A9.3: false hides the depot tab and its switcher (Figma
   * "Foodservice header - One Account only", 5081:54377). Default true. The active depot's
   * cut-off pill and announcements (Story 8.5) still render in the tab's place.
   */
  showDepotSwitcher?: boolean;
  /**
   * ADO-3749: the User Menu's person, account and sign-out (from `useHeaderChrome`). Absent,
   * the menu shows the neutral "Account" chip with no account and no Sign out.
   */
  userMenu?: SiteHeaderUserMenu | undefined;
  // Explicitly `| undefined` (not just optional): `useHeaderChrome` types
  // `depotName` as `string | undefined` because a depot whose name has not
  // resolved yet is a real state, and under `exactOptionalPropertyTypes` an
  // optional prop does NOT accept an explicit undefined. Without this, every
  // `<SiteHeader {...useCatalogueHeader()} />` spread is a type error — it was
  // five of them across Products, MyAccount, AdminUsers, CatalogueLinkingHeader
  // and Orders. The consumer below already reads it as possibly-undefined.
  depotName?: string | undefined;
  /**
   * Story 8.5 (ADO-2014), D-8: the session cart's cut-off, from `useDepotCutOff`.
   * Rendered as the small `CutOffPill` beside the depot name, in the
   * default AND the themed placement (D-10). `undefined`, a `null` status (loading or
   * failed) and NONE render nothing at all (AC4).
   */
  depotCutOff?:
    | {
        readonly status: CutOffPillStatus | null;
        readonly countdown: CutOffPillCountdown | null;
      }
    | undefined;
  /**
   * Story 8.5 (ADO-2014), D-20: each Depot Switcher option's cut-off pill, at the size the
   * panel asks for (`small` on the band, `compact` on the dropdown). The host renders it
   * (`useHeaderChrome`), so the header does no fetching; absent, the options carry none.
   */
  renderOptionCutOff?: ((optionId: string, size: CutOffPillSize) => ReactNode) | undefined;
  /** Overlay slot beneath the sub-nav (browse mega-menu). */
  children?: ReactNode;
  /**
   * Search typeahead wiring (Epic 7 pixel parity, ADO-903). The dropdown UI is
   * app-owned (it needs the search client + basket/availability stores, which
   * the design system must not import) and renders through this slot into the
   * search box's relative anchor, so it stays glued under the input in both the
   * unscrolled and pinned header states. `onSearchFocus` lets the page open the
   * dropdown on focus.
   *
   * PR #139 review: the input is a plain search field, NOT an ARIA combobox. The
   * suggestions are a labelled group of ordinary buttons the user tabs through —
   * there is no arrow-key/`aria-activedescendant` selection to back a combobox
   * promise, so the input advertises none (a false combobox is worse than none).
   */
  searchTypeahead?: ReactNode;
  onSearchFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Ref to the search input, so the owner can move focus into the search box — e.g.
   *  the scan modal's "Search by name" fallback returning the user to text search
   *  (Story 7.18, AC7/AC12). */
  searchInputRef?: Ref<HTMLInputElement>;
  /**
   * Manager-dashboard entry (Story 6.31, ADO-1253). When true, the branding bar
   * shows a "Manager dashboard" link to `/admin/users`. Callers pass this ONLY
   * when the signed-in user can reach that route (admin role via `isAdminRole`);
   * the server re-checks the route (NFR18) — this is DISPLAY/ROUTING only. No
   * count badge: the Figma badge has no truthful source (never fake data).
   */
  showManagerDashboard?: boolean;
  /**
   * Barcode scan trigger (Story 7.18, ADO-1387, AC1). The camera control is
   * CAPABILITY-GATED and appears ONLY where a scan can actually be performed:
   *   • `onScanClick` omitted → the control is ABSENT (this surface does not offer
   *     scanning), and text search is unaffected.
   *   • `onScanClick` set + `scanAvailable === false` → the control is DISABLED with a
   *     stated reason (`scanUnavailableReason`), never an enabled no-op (AC1).
   *   • `onScanClick` set + `scanAvailable !== false` → an enabled icon button that
   *     opens the scanner; `scanActive` advertises the open dialog (aria-expanded).
   */
  onScanClick?: () => void;
  scanAvailable?: boolean;
  scanUnavailableReason?: string;
  scanActive?: boolean;
  /**
   * Cart trigger (Story 8.2, ADO-2011). When `onCartClick` is wired the cart button
   * is functional: it shows `cartCount` (the cart's distinct lines), opens the cart
   * side panel (a dialog: `cartExpanded` / `cartPanelId` drive aria-expanded /
   * aria-controls) and the host renders the panel through `cartPanel`. Without it
   * the button stays the visual placeholder.
   */
  onCartClick?: () => void;
  /** The number of distinct lines in the cart, which the badge shows on every layout
   *  (capped at "99+"); the panel's own header carries the case total. Null when the
   *  count is not known (the basket has not loaded, or its read failed): the trigger
   *  then shows UNKNOWN_TOTAL, never a false 0. */
  cartCount?: number | null;
  /** The cart value, summed from the prices the server locked
   *  on the lines. Null or omitted when it is not known: the button then shows
   *  UNKNOWN_TOTAL, never a false €0.00, and the label leaves the value out. */
  cartValue?: MoneyValue | null;
  cartExpanded?: boolean;
  cartPanelId?: string;
  /** The app-owned cart side panel (the design system does not read the basket). */
  cartPanel?: ReactNode;
  /**
   * Story 8.10 (ADO-2019), AC1 / AC2: the "HHT Orders" link to `/hht-orders` (the 8.10
   * Figma exports: device mark, label and an orange count badge, on the blue bar before
   * the account chip; in the top bar on mobile). Rendered ONLY when passed: the host
   * passes it when the HHT workflow applies (server-decided). `waitingCount` 0 shows the
   * link with no badge (AC2: where the order will appear).
   */
  hhtOrders?: { readonly waitingCount: number } | undefined;
  /**
   * Story 18.19 (ADO-4086), AC11: the "RBP Shopping List" action, after "HHT Orders" and
   * before the account chip (in the top bar on mobile, as the device mark is). Rendered ONLY
   * when passed: the host passes it when the server says the session takes part in RBP,
   * whatever the HHT workflow says.
   */
  rbpShoppingList?: { readonly to: string } | undefined;
  /**
   * Story 7.33 (ADO-2026): the "Planogram" entry beside Offers (Figma Planogram Button,
   * 6603:97683). Rendered ONLY when passed: the host passes it when the server says the shop is
   * offered shelf plans (SL1.0's visibility rule, server-decided). On a phone the listing's
   * toolbar carries the entry instead, as the mobile frames draw it.
   */
  planogramNav?: { readonly to: string } | undefined;
}

export function SiteHeader({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
  onNavigateHome,
  logo,
  themed = false,
  favouritesCount = 0,
  onBrowseClick,
  browseExpanded,
  browseMenuId,
  onBrowseDismiss,
  depots,
  activeDepotId,
  onSelectDepot,
  depotName,
  depotCutOff,
  renderOptionCutOff,
  depotDetail,
  depotsNav,
  showDepotSwitcher = true,
  userMenu,
  children,
  searchTypeahead,
  onSearchFocus,
  searchInputRef,
  showManagerDashboard = false,
  onScanClick,
  scanAvailable = true,
  scanUnavailableReason,
  scanActive = false,
  onCartClick,
  cartCount = 0,
  cartValue = null,
  cartExpanded = false,
  cartPanelId,
  cartPanel,
  hhtOrders,
  rbpShoppingList,
  planogramNav,
}: SiteHeaderProps) {
  const searchInputId = useId();
  const scanReasonId = useId();
  const browseIsMenu = browseExpanded !== undefined;
  const layout = useHeaderLayout();
  const mobile = layout === 'mobile';

  // Scroll-minimize (ADO-903): detect the pinned state and publish the live
  // header offset. Measured values only — the branding bar + sub-nav rects (the
  // <header> is `display: contents`, so it has no box of its own) via a scroll
  // listener + ResizeObserver, covering the <768px reflow (search on its own
  // row) and any depot/badge-driven height change. `data-stuck` gates the drop
  // shadow to the actually-pinned state.
  const brandingRef = useRef<HTMLDivElement>(null);
  const subNavRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const brandingEl = brandingRef.current;
    const subNavEl = subNavRef.current;
    if (!brandingEl || !subNavEl) return;

    const update = () => {
      const subNavRect = subNavEl.getBoundingClientRect();
      // Sticky pins the bar at top 0 the instant its top edge reaches the
      // viewport top; the scrollY guard keeps zero-height test/layout
      // environments (where every rect is 0) reading as "not pinned".
      const pinned = window.scrollY > 0 && subNavRect.top <= 0;
      setStuck(pinned);
      const offset = pinned
        ? subNavRect.height
        : brandingEl.getBoundingClientRect().height + subNavRect.height;
      document.documentElement.style.setProperty(
        '--shoplink-header-offset',
        `${Math.round(offset)}px`,
      );
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(brandingEl);
    ro?.observe(subNavEl);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
      document.documentElement.style.removeProperty('--shoplink-header-offset');
    };
  }, []);

  // Depot selector (Story 7-FU-F) — a functional dropdown when there is more than
  // one depot to pick and a handler is wired; otherwise a static tab.
  const canSwitchDepot = onSelectDepot !== undefined && (depots?.length ?? 0) > 1;
  const [depotOpen, setDepotOpen] = useState(false);
  // ADO-3749 AC13: one header menu at a time. Opening the User Menu closes the depot switcher,
  // and opening the switcher closes the User Menu.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const depotRef = useRef<HTMLDivElement>(null);
  const depotPanelRef = useRef<HTMLDivElement>(null);
  const depotTriggerRef = useRef<HTMLButtonElement>(null);
  const depotPanelId = useId();
  // Story 8.1 (ADO-1092), AC1 — when real depots ARE wired but none is selected
  // yet (the multi-depot gate), the tab PROMPTS ("Select a depot") and hides the
  // cut-off countdown instead of showing a name + timer for a depot the user has
  // not chosen. This gate applies on EVERY full-SiteHeader screen that wires depots
  // via useCatalogueHeader/useHeaderChrome — Products AND My Account / Admin Users —
  // so a multi-depot user who hasn't picked sees the prompt there too. Pure-
  // placeholder mode is only a header rendered with NO depots wired (the demo /
  // unauthenticated placeholder): it still shows the demo VC Dublin chrome.
  const hasWiredDepots = (depots?.length ?? 0) > 0;
  const noDepotSelected = hasWiredDepots && (activeDepotId == null || activeDepotId === '');
  const displayDepotName = noDepotSelected ? 'Select a depot' : (depotName ?? DEPOT_NAME);
  const showDetail = !noDepotSelected && depotDetail !== undefined && depotDetail !== '';
  // Figma VC Switcher 5083:7623 "Icon": the active option's depot icon (its accent colour
  // is drawn into the asset), the same one its switcher tile shows. None while no depot is
  // chosen, or for an option without one (the session-depot fallback).
  const activeDepotIconSrc = noDepotSelected
    ? null
    : (depots?.find((depot) => depot.id === activeDepotId)?.iconSrc ?? null);

  useEffect(() => {
    if (!depotOpen) return;
    // Dismiss on an outside CLICK, not mousedown: on desktop the switcher band sits in the
    // page flow above the nav row, so closing it on mousedown moves every control below it
    // (Cart, Orders, the search box) up under the pointer before the mouseup, and the click
    // lands on whatever is there instead. On click, the pressed control has already received
    // it. (The tablet / mobile dropdown floats, so nothing moves there.)
    const onOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (depotRef.current?.contains(target) || depotPanelRef.current?.contains(target)) return;
      setDepotOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // PR #139 review: return focus to the trigger on Escape so it never drops to
      // <body> (WCAG 2.4.3) — the depot list is a disclosure, not an ARIA menu.
      // preventDefault claims the Escape so a window-level layer (the upsell toast)
      // does not also dismiss on it (PR #279 review).
      if (e.key === 'Escape') {
        e.preventDefault();
        setDepotOpen(false);
        depotTriggerRef.current?.focus();
      }
    };
    document.addEventListener('click', onOutsideClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onOutsideClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [depotOpen]);

  // Browse mega-menu dismissal. The overlay is owner-rendered into `children`, so
  // the header never owns the open state: it detects the dismissing gesture and
  // calls back. Escape / re-toggling Browse / picking a node stay with the owner.
  const browseTriggerRef = useRef<HTMLButtonElement>(null);
  const canDismissBrowse = onBrowseDismiss !== undefined;
  // The callback lives in a ref because owners pass an inline arrow: a new identity
  // every render would re-run the effect and clear a hover-close timer mid-flight,
  // so the pending dismissal would never fire.
  const browseDismissRef = useRef(onBrowseDismiss);
  useEffect(() => {
    browseDismissRef.current = onBrowseDismiss;
  });
  // The focus-restore tick lives OUTSIDE the dismissal effect below: dismissing
  // flips `browseExpanded` false, so that effect's cleanup runs as a direct
  // consequence of the dismissal and would cancel the very restore it just
  // scheduled. Cleared on unmount instead, where nothing is left to focus.
  const browseFocusRestoreRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(browseFocusRestoreRef.current), []);

  useEffect(() => {
    if (!browseIsMenu || !browseExpanded || !canDismissBrowse) return;
    const subNavEl = subNavRef.current;
    const overlayOf = () => (browseMenuId ? document.getElementById(browseMenuId) : null);
    const dismiss = () => {
      // WCAG 2.4.3: dismissing unmounts the overlay, so focus sitting on a control
      // INSIDE it would silently drop to <body>. Restoring it to the trigger has to
      // be DEFERRED and CONDITIONAL, because this also runs inside the mousedown
      // listener and the browser's own focus handling comes after us: it focuses a
      // focusable mousedown target, and blurs to <body> for a non-focusable one, so
      // an immediate focus() would be undone on exactly the common "click away onto
      // ordinary page content" gesture. One tick later both that and React's unmount
      // have settled, and focus resting on <body> is the tell that nothing else
      // claimed it. Anything that did (the control the user actually clicked) keeps
      // it, which is why this never steals focus outright.
      const hadOverlayFocus = overlayOf()?.contains(document.activeElement) ?? false;
      browseDismissRef.current?.();
      if (!hadOverlayFocus) return;
      clearTimeout(browseFocusRestoreRef.current);
      browseFocusRestoreRef.current = setTimeout(() => {
        browseFocusRestoreRef.current = undefined;
        const active = document.activeElement;
        if (active === null || active === document.body) browseTriggerRef.current?.focus();
      }, 0);
    };

    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The trigger is EXCLUDED so it keeps plain toggle semantics: closing here on
      // mousedown and then toggling on the click that follows would reopen the menu
      // the user just asked to shut.
      if (browseTriggerRef.current?.contains(target)) return;
      if (overlayOf()?.contains(target)) return;
      dismiss();
    };

    // Hover-out. The overlay renders INSIDE the sub-nav div (the `children` slot),
    // so moving the pointer from the trigger down into the menu never fires
    // mouseleave; only leaving for the branding bar or the page content does.
    // Keyboard-only users are untouched: mouseleave cannot fire unless a pointer
    // entered the sub-nav in the first place. The mobile composition is a touch
    // layout whose trigger (the bottom tab bar) and side tray both sit OUTSIDE the
    // sub-nav, so there a hover-out would close the tray as soon as it opened: it
    // is dismissed by tap only.
    let hoverCloseTimer: ReturnType<typeof setTimeout> | undefined;
    const cancelHoverClose = () => {
      if (hoverCloseTimer !== undefined) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = undefined;
    };
    const onLeave = () => {
      cancelHoverClose();
      hoverCloseTimer = setTimeout(dismiss, BROWSE_HOVER_CLOSE_GRACE_MS);
    };

    const hoverTarget = mobile ? null : subNavEl;
    document.addEventListener('mousedown', onDown);
    hoverTarget?.addEventListener('mouseleave', onLeave);
    hoverTarget?.addEventListener('mouseenter', cancelHoverClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      hoverTarget?.removeEventListener('mouseleave', onLeave);
      hoverTarget?.removeEventListener('mouseenter', cancelHoverClose);
      cancelHoverClose();
    };
  }, [browseIsMenu, browseExpanded, canDismissBrowse, browseMenuId, mobile]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSearchSubmit();
  };

  const favLabel = favouritesCount > 0 ? `Favourites, ${favouritesCount} saved` : 'Favourites';

  // Depot switcher. Its home is the blue branding bar: a tab hanging off the bar's
  // bottom edge on desktop, a white box on tablet and mobile (CSS). A themed
  // tenant lifts the logo into a white top bar and, on desktop / tablet, moves
  // this control DOWN into the brand-coloured nav row (Story 6.35 design update)
  // — same control, same dropdown/gate behaviour, restyled inline via
  // `data-themed`. The cut-off pill shows in both placements (Story 8.5 D-10, so
  // AC1 holds for themed tenants); the active depot name + selection gate are
  // preserved.
  // Story 8.1 (ADO-2010), Task 6.1 — the Depot Switcher. On desktop a band rendered in
  // flow between the branding bar and the sub-nav (Figma 5080:7247), pushing the sub-nav
  // down. On tablet and mobile a dropdown inside the depot box, over the page, so the
  // header keeps its height (Figma 5405:22656, 5332:67565). Picking closes it and
  // returns focus to the tab (WCAG 2.4.3).
  // ADO-2262: it stays mounted through its exit (`useExitTransition`), after a pick too, while
  // `aria-expanded` and focus change at once. The dropdown fades and lifts away (CSS, ending
  // on its own `transitionend`); the band collapses through `Collapse`, which grows it in as it
  // mounts and reports when it has collapsed.
  const depotDropdown = layout !== 'desktop';
  const depotExit = useExitTransition(
    canSwitchDepot && depotOpen && showDepotSwitcher,
    depotDropdown ? MOTION_MS.exitBase : MOTION_MS.base,
  );
  const depotPanel = depotExit.present ? (
    <DepotSwitcherPanel
      id={depotPanelId}
      panelRef={depotPanelRef}
      options={depots ?? []}
      activeId={activeDepotId}
      variant={depotDropdown ? 'dropdown' : 'band'}
      renderOptionCutOff={renderOptionCutOff}
      closing={depotDropdown && depotExit.closing}
      onExited={depotExit.finish}
      onSelect={(id) => {
        onSelectDepot?.(id);
        setDepotOpen(false);
        depotTriggerRef.current?.focus();
      }}
    />
  ) : null;

  const depotInNav = themed && !mobile;
  const depotSwitcher = (
    <div className={styles.depotTab} data-themed={depotInNav ? 'true' : undefined} ref={depotRef}>
      <button
        ref={depotTriggerRef}
        type="button"
        className={styles.depotTrigger}
        // PR #139 review: a plain disclosure toggle (aria-expanded), NOT an
        // ARIA menu — the menu keyboard model (arrow keys / roving tabindex /
        // focus management) was never implemented, so advertising `menu`
        // roles over-promised. The list below is an ordinary button group.
        aria-expanded={canSwitchDepot ? depotOpen : undefined}
        aria-controls={canSwitchDepot && depotOpen ? depotPanelId : undefined}
        aria-label={
          noDepotSelected
            ? 'Select a depot'
            : canSwitchDepot
              ? `Change depot, current ${displayDepotName}${showDetail ? `, ${depotDetail}` : ''}`
              : `Depot: ${displayDepotName}${showDetail ? `, ${depotDetail}` : ''}`
        }
        onClick={
          canSwitchDepot
            ? () => {
                if (!depotOpen) setUserMenuOpen(false);
                setDepotOpen(!depotOpen);
              }
            : undefined
        }
      >
        {activeDepotIconSrc ? (
          <img
            className={styles.depotIcon}
            src={activeDepotIconSrc}
            width={16}
            height={16}
            alt=""
          />
        ) : null}
        {showDetail ? (
          <span className={styles.depotLabelStack}>
            <span className={styles.depotName}>{displayDepotName}</span>
            <span className={styles.depotDetail}>{depotDetail}</span>
          </span>
        ) : (
          <span className={styles.depotName}>{displayDepotName}</span>
        )}
        {/* Story 8.5 (ADO-2014): the cut-off pill, hidden until a depot is chosen
            (Story 8.1 gate) and whenever there is no cut-off to show (AC4). */}
        {!noDepotSelected && depotCutOff !== undefined && (
          <CutOffPill
            status={depotCutOff.status}
            countdown={depotCutOff.countdown}
            phoneSize="compact"
          />
        )}
        {canSwitchDepot && (
          <Icon
            name="caretDown"
            size={16}
            className={depotOpen ? `${styles.caret} ${styles.caretOpen}` : styles.caret}
          />
        )}
      </button>
      {/* The polite cut-off announcements (60, 30, 10 minutes, passing), outside the
          trigger button whose children are presentational to assistive tech. */}
      {!noDepotSelected && depotCutOff !== undefined && (
        <CutOffAnnouncer status={depotCutOff.status} countdown={depotCutOff.countdown} />
      )}
      {depotDropdown && depotPanel}
    </div>
  );

  // Story 8.5 (ADO-2014) AC1 while the depot tab is hidden (Story 8.1 A9.3, one account and
  // nothing to switch to): the cut-off pill and its announcements keep the tab's slot.
  const cutOffOnly =
    !noDepotSelected && depotCutOff !== undefined ? (
      <div className={styles.depotCutOffOnly} data-themed={depotInNav ? 'true' : undefined}>
        <CutOffPill
          status={depotCutOff.status}
          countdown={depotCutOff.countdown}
          phoneSize="compact"
        />
        <CutOffAnnouncer status={depotCutOff.status} countdown={depotCutOff.countdown} />
      </div>
    ) : null;

  // Story 8.5 (ADO-2014), D-22: once the branding bar has scrolled away (`stuck`), the depot
  // tab's pill is out of view, so the pinned bar shows the same pill beside the cart on
  // desktop and tablet (small). Not when the depot already sits in the pinned bar (themed
  // desktop and tablet, and every phone, whose depot row is pinned, D-23), and only while
  // there is a pill to show. The announcer stays the depot tab's.
  const pinnedCutOffShown =
    stuck &&
    !mobile &&
    !depotInNav &&
    !noDepotSelected &&
    depotCutOff !== undefined &&
    isCutOffShown(depotCutOff.status, depotCutOff.countdown);
  const pinnedCutOff = pinnedCutOffShown ? (
    <CutOffPill status={depotCutOff.status} countdown={depotCutOff.countdown} phoneSize="compact" />
  ) : null;

  // Browse trigger — in the sub-nav on desktop / tablet, in the bottom tab bar on
  // mobile. Exactly one renders, and it carries `browseTriggerRef` (the dismissal
  // effect's toggle exclusion + focus-restore target) wherever it is.
  // The popup is BrowseNav / BrowseTray — plain buttons, not an ARIA menu widget
  // (no role=menu / arrow-key / roving-tabindex model). So it advertises the
  // generic `aria-haspopup="true"` (not `"menu"`), with aria-controls pointing at
  // the overlay container while it's open (PR #139 review). `aria-label` names it
  // at every width, where the visible text may be hidden.
  const browseTriggerProps = {
    ref: browseTriggerRef,
    type: 'button' as const,
    'aria-haspopup': browseIsMenu ? true : undefined,
    'aria-expanded': browseIsMenu ? browseExpanded : undefined,
    'aria-controls': browseIsMenu && browseExpanded ? browseMenuId : undefined,
    'aria-label': 'Browse',
    onClick: onBrowseClick,
  };

  // Depots (Epic 8) — Story 8.1 (ADO-2010): a real link when the host wires
  // `depotsNav` (the choice screen, or "Home" to the landing page for a single-account
  // Customer, A9.3). Otherwise the visual placeholder, stated as such to assistive tech.
  const depotsPlaceholderProps = {
    type: 'button' as const,
    'data-placeholder': 'depots',
    'aria-label': 'Depots (coming soon)',
  };
  const depotsNavIcon = depotsNav?.label === 'Home' ? 'storefront' : 'depots';
  const renderDepotsNav = (className: string | undefined, labelled: boolean) =>
    depotsNav === null ? null : depotsNav !== undefined ? (
      <Link
        to={depotsNav.to}
        className={className}
        aria-label={labelled ? undefined : depotsNav.label}
      >
        <Icon name={depotsNavIcon} size={16} className={labelled ? styles.navIcon : undefined} />
        {labelled && <span>{depotsNav.label}</span>}
      </Link>
    ) : (
      <button {...depotsPlaceholderProps} className={className}>
        <Icon name="depots" size={16} className={labelled ? styles.navIcon : undefined} />
        {labelled && <span>Depots</span>}
      </button>
    );

  // Story 8.10 (ADO-2019), AC1 / AC2: the "HHT Orders" link, only where the host says
  // the HHT workflow applies (the markup is shared with the chooser header, DV-37).
  const hhtLink =
    hhtOrders === undefined ? null : (
      <HhtOrdersLink
        waitingCount={hhtOrders.waitingCount}
        classes={{ link: styles.hhtLink, label: styles.hhtLabel, badge: styles.hhtBadge }}
      />
    );
  // Story 18.19 (ADO-4086), AC11: the RBP action takes the HHT link's look and place rules.
  const rbpLink =
    rbpShoppingList === undefined ? null : (
      <RbpShoppingListLink
        to={rbpShoppingList.to}
        classes={{ link: `${styles.hhtLink} ${styles.rbpLink}`, label: styles.hhtLabel }}
      />
    );

  // Cart. The blue labelled button on desktop, an icon item in the tabs row on
  // tablet (CSS), and a bare icon beside the depot box on mobile. The cart is
  // rendered once, where the composition draws it (the sub-nav's right group, or
  // the mobile depot row). Where the host wires `onCartClick` it opens the cart
  // side panel, counts the cart's lines on its badge and shows the
  // host's `cartValue` under "Cart" (Figma 4002:14778), which the label names too;
  // otherwise it stays the placeholder, with no total. Every layout draws the badge
  // (mobile 5374:17097, tablet 5329:14496, desktop 4976:1664), "99+" past 99 lines;
  // mobile draws no "Cart" label or total.
  const cartWired = onCartClick !== undefined;
  const cartTotal = cartWired && cartValue !== null ? formatMoney(cartValue) : null;
  // ADO-2262: the badge pops when the count grows from a number it was already showing (not on
  // the first count after an unknown one, nor on a drop). A further increase mid-pop restarts
  // it: the mark is dropped and, after a reflow, set again. The CSS runs the pop, and leaves it
  // out under reduced motion.
  const cartCountRef = useRef<HTMLSpanElement>(null);
  const shownCartCountRef = useRef(cartWired ? cartCount : null);
  useEffect(() => {
    const previous = shownCartCountRef.current;
    const current = cartWired ? cartCount : null;
    shownCartCountRef.current = current;
    const badge = cartCountRef.current;
    if (previous === null || current === null || current <= previous || badge === null) return;
    badge.removeAttribute('data-pop');
    badge.getBoundingClientRect();
    badge.setAttribute('data-pop', '');
  }, [cartWired, cartCount]);
  const cartLines = cartCount === null ? 'lines unknown' : linesLabel(cartCount);
  const cartBadge = !cartWired
    ? CART_COUNT
    : cartCount === null
      ? UNKNOWN_TOTAL
      : cartCount > CART_BADGE_MAX
        ? `${CART_BADGE_MAX}+`
        : String(cartCount);
  const cartButton = (
    <button
      type="button"
      className={styles.cartButton}
      {...(cartWired
        ? {
            'aria-label':
              cartTotal === null
                ? `Open cart, ${cartLines}`
                : `Open cart, ${cartLines}, ${cartTotal}`,
            'aria-haspopup': 'dialog' as const,
            'aria-expanded': cartExpanded,
            // Only while open: the side panel is not in the DOM when closed.
            ...(cartExpanded && cartPanelId !== undefined ? { 'aria-controls': cartPanelId } : {}),
            onClick: onCartClick,
          }
        : {
            'data-placeholder': 'cart',
            'aria-label': `Basket, ${CART_COUNT} items (coming soon)`,
          })}
    >
      <span className={styles.cartIconWrap}>
        <Icon name="cart" size={mobile ? 22 : 20} />
        <span
          ref={cartCountRef}
          className={styles.cartCount}
          data-part="cart-count"
          aria-hidden="true"
          onAnimationEnd={(event) => event.currentTarget.removeAttribute('data-pop')}
        >
          {cartBadge}
        </span>
      </span>
      {!mobile && (
        <span className={styles.cartText}>
          <span className={styles.cartLabel}>Cart</span>
          <span className={styles.cartTotal} data-testid="header-cart-total" aria-hidden>
            {cartTotal ?? UNKNOWN_TOTAL}
          </span>
        </span>
      )}
    </button>
  );

  // Bottom tab bar item class: the open tray marks Browse as the current tab, so
  // no route tab competes with it while it is open.
  const tabClass = ({ isActive }: { isActive: boolean }) =>
    isActive && !browseExpanded ? `${styles.tab} ${styles.tabActive}` : styles.tab;

  return (
    <header className={styles.header}>
      {/* ── Branding bar (scrolls away with the page). Blue for the default brand;
            WHITE with the brand logo when themed (Story 6.35). ── */}
      <div
        className={styles.brandingBar}
        data-themed={themed ? 'true' : undefined}
        ref={brandingRef}
      >
        <div
          className={styles.brandingInner}
          data-hht={hhtLink !== null ? 'true' : undefined}
          data-rbp={rbpLink !== null ? 'true' : undefined}
        >
          <Link to="/products" className={styles.logo} onClick={onNavigateHome}>
            <img
              src={logo?.src ?? '/images/logo.svg'}
              alt={logo?.alt ?? 'ShopLink'}
              className={styles.logoImg}
            />
          </Link>

          {!depotInNav && !mobile && (showDepotSwitcher ? depotSwitcher : cutOffOnly)}

          {mobile && renderDepotsNav(`${styles.brandingIcon} ${styles.depotsIcon}`, false)}

          {/* The "Store" landmark only renders when at least one of its controls
              would show; with both gated OFF it must not emit an empty named nav
              (an empty landmark is still announced by screen readers). */}
          {(MANAGER_DASHBOARD_ENABLED && showManagerDashboard) || MY_SECTION_ENABLED ? (
            <nav className={styles.utilityNav} aria-label="Store">
              {/* Manager dashboard (Story 6.31, ADO-1253) — admin-only entry to the
                  user-management screen. Rendered only when the caller signals the
                  user can reach /admin/users; no count badge (never fake data).
                  Currently gated OFF by MANAGER_DASHBOARD_ENABLED while the header
                  placement is on hold; the showManagerDashboard wiring is preserved. */}
              {MANAGER_DASHBOARD_ENABLED && showManagerDashboard ? (
                <Link to="/admin/users" className={styles.managerDash}>
                  <span className={styles.managerDashDot} aria-hidden />
                  Manager dashboard
                </Link>
              ) : null}
              {/* "My section" (placeholder) — a coming-soon control with no
                  destination yet. Gated OFF by MY_SECTION_ENABLED while there is
                  nowhere for it to go; the markup is preserved for when a real
                  section lands. */}
              {MY_SECTION_ENABLED ? (
                <button
                  type="button"
                  className={styles.mySection}
                  aria-label="My section (coming soon)"
                >
                  My section
                </button>
              ) : null}
            </nav>
          ) : null}

          {hhtLink}
          {rbpLink}

          {/* ADO-3749: the User Menu, top right: the chip on desktop / tablet, the
              initials avatar on mobile (where the person icon was). */}
          <UserMenu
            className={styles.userMenu}
            layout={layout}
            user={userMenu?.user ?? null}
            account={userMenu?.account ?? null}
            onSignOut={userMenu?.onSignOut}
            themed={themed}
            open={userMenuOpen}
            onOpenChange={(next) => {
              if (next) setDepotOpen(false);
              setUserMenuOpen(next);
            }}
          />
        </div>
      </div>

      {!depotDropdown && depotPanel !== null && (
        <Collapse open={!depotExit.closing} appear fade={false} onClosed={depotExit.finish}>
          {depotPanel}
        </Collapse>
      )}

      {/* ── Sub-nav (alone pins to the viewport top). White for the default brand
            on desktop; the tabs row + search row on tablet; the depot row + search
            row on mobile. The BRAND-coloured nav row when themed (Story 6.35). ── */}
      <div
        className={styles.subNav}
        data-themed={themed ? 'true' : undefined}
        ref={subNavRef}
        data-stuck={stuck ? 'true' : 'false'}
      >
        {/* Story 8.5 (ADO-2014), D-23: on mobile the branding bar's second row (the depot
            box, with its cut-off pill, and the cart) is drawn here, in the branding bar's
            colours, so it stays pinned above the search row while the logo row scrolls
            away. Unscrolled, the header looks as when the row sat in the bar's grid. */}
        {mobile && (
          <div className={styles.mobileDepotRow} data-themed={themed ? 'true' : undefined}>
            {showDepotSwitcher ? depotSwitcher : cutOffOnly}
            {cartButton}
          </div>
        )}
        <div className={styles.subNavInner}>
          {!mobile && (
            <div className={styles.primaryNav}>
              {depotInNav && (showDepotSwitcher ? depotSwitcher : cutOffOnly)}
              {renderDepotsNav(styles.navItem, true)}
              <Link to="/products" className={styles.navItem} onClick={onNavigateHome}>
                <Icon name="storefront" size={16} className={styles.navIcon} />
                <span>Shop</span>
              </Link>
              <button {...browseTriggerProps} className={styles.navItem}>
                <Icon name="menu" size={16} className={styles.navIcon} />
                <span>Browse</span>
              </button>
            </div>
          )}

          {/* The row is the full-width white search band on tablet / mobile (a
              boxless `display: contents` on desktop). The anchor inside owns the
              search box's flex sizing AND is the relative positioning context the
              typeahead dropdown hangs from — so the dropdown is always the
              input's width, in every header state. */}
          <div className={styles.searchRow}>
            <div className={styles.searchAnchor}>
              <form className={styles.searchForm} role="search" onSubmit={handleSubmit}>
                <label htmlFor={searchInputId} className={styles.visuallyHidden}>
                  Search products
                </label>
                <span className={styles.searchIcon} aria-hidden>
                  <Icon name="search" size={16} />
                </span>
                <input
                  ref={searchInputRef}
                  id={searchInputId}
                  type="search"
                  className={styles.searchInput}
                  value={searchValue}
                  onChange={(e) => onSearchChange(e.target.value)}
                  onFocus={onSearchFocus}
                  // AC11 (Story 7.18): no voice/audio affordance — the placeholder
                  // never implies "speak". Voice search is descoped. Wording per
                  // Figma: desktop Sub Header vs the tablet / mobile search row.
                  placeholder={layout === 'desktop' ? 'Search for products' : 'Search products…'}
                  maxLength={MAX_SEARCH_QUERY_LENGTH}
                  autoComplete="off"
                />
                <div className={styles.searchTrailing}>
                  {searchValue !== '' && onClearSearch ? (
                    <button
                      type="button"
                      className={styles.searchIconBtn}
                      onClick={onClearSearch}
                      aria-label="Clear search"
                    >
                      <Icon name="x" size={16} />
                    </button>
                  ) : null}
                  {/* Camera / scan (Story 7.18, ADO-1387, AC1). Capability-gated: the
                    control is ABSENT unless the surface wires `onScanClick`, and
                    DISABLED WITH A STATED REASON where the device cannot scan — never
                    the previous enabled no-op. */}
                  {onScanClick ? (
                    scanAvailable ? (
                      <button
                        type="button"
                        className={styles.searchIconBtn}
                        onClick={onScanClick}
                        aria-label="Scan a barcode"
                        aria-haspopup="dialog"
                        aria-expanded={scanActive}
                      >
                        <Icon name="camera" size={16} />
                      </button>
                    ) : (
                      // AC1 "keyboard-focusable, disabled with a stated reason": a native
                      // `disabled` button is removed from the tab order (AT/keyboard users
                      // can never reach it to learn WHY it's off) and its `title` never
                      // shows on touch. Use `aria-disabled` so it stays focusable + in the
                      // tab order, associate the reason with `aria-describedby` (announced
                      // on focus, not just buried in a label a skipped control never
                      // reaches), and omit `onClick` so activating it is a no-op.
                      <>
                        <button
                          type="button"
                          className={styles.searchIconBtn}
                          aria-disabled="true"
                          aria-label="Scan a barcode"
                          aria-describedby={scanReasonId}
                          title={scanUnavailableReason ?? undefined}
                        >
                          <Icon name="camera" size={16} />
                        </button>
                        <span id={scanReasonId} className={styles.visuallyHidden}>
                          {scanUnavailableReason ?? 'Scanning is unavailable on this device'}
                        </span>
                      </>
                    )
                  ) : null}
                </div>
              </form>
              {searchTypeahead}
            </div>
          </div>

          {!mobile && (
            <div
              className={styles.rightGroup}
              data-pinned-cut-off={pinnedCutOff !== null ? 'true' : undefined}
            >
              <nav className={styles.iconNav} aria-label="Account">
                {/* Story 9.4 (ADO-1948): Orders is a real route, and it leads the
                  group (Orders, Favourites, Offers) as Figma draws it. */}
                <Link to="/orders" className={styles.iconItem} aria-label="Orders">
                  <span className={styles.iconWrap}>
                    <Icon name="clock" size={20} />
                  </span>
                  <span className={styles.iconLabel}>Orders</span>
                </Link>
                <Link to="/favourites" className={styles.iconItem} aria-label={favLabel}>
                  <span className={styles.iconWrap}>
                    <Icon name="heart" size={20} />
                    {favouritesCount > 0 ? (
                      <span className={styles.badgeAmber} aria-hidden>
                        {favouritesCount}
                      </span>
                    ) : null}
                  </span>
                  <span className={styles.iconLabel}>Favourites</span>
                </Link>
                <button
                  type="button"
                  className={styles.iconItem}
                  data-placeholder="offers"
                  aria-label={`Offers, ${OFFERS_COUNT} (coming soon)`}
                >
                  <span className={styles.iconWrap}>
                    <Icon name="tag" size={20} />
                    <span className={styles.badgeAmber} aria-hidden>
                      {OFFERS_COUNT}
                    </span>
                  </span>
                  <span className={styles.iconLabel}>Offers</span>
                </button>
                {planogramNav !== undefined && (
                  <NavLink
                    to={planogramNav.to}
                    className={({ isActive }) =>
                      isActive ? `${styles.iconItem} ${styles.iconItemActive}` : styles.iconItem
                    }
                    aria-label="Planogram"
                  >
                    <span className={styles.iconWrap}>
                      <Icon name="rows" size={20} />
                    </span>
                    <span className={styles.iconLabel}>Planogram</span>
                  </NavLink>
                )}
              </nav>
              <div className={styles.cartCluster}>
                {pinnedCutOff !== null ? (
                  <span className={styles.pinnedCutOff} data-testid="header-pinned-cut-off">
                    {pinnedCutOff}
                  </span>
                ) : null}
                {cartButton}
              </div>
            </div>
          )}
        </div>
        {children}
      </div>

      {/* ── Mobile bottom tab bar (Figma 4002:17475): the primary destinations,
            fixed to the viewport bottom. Figma draws no count badges here; the
            favourites count stays in the link's accessible name. ── */}
      {mobile && (
        <nav className={styles.tabBar} aria-label="Main">
          <NavLink
            to="/products"
            end
            className={tabClass}
            onClick={onNavigateHome}
            aria-label="Shop"
          >
            <Icon name="storefront" size={24} />
            <span className={styles.tabLabel}>Shop</span>
          </NavLink>
          <button
            {...browseTriggerProps}
            className={browseExpanded ? `${styles.tab} ${styles.tabActive}` : styles.tab}
          >
            <Icon name="menu" size={24} />
            <span className={styles.tabLabel}>Browse</span>
          </button>
          <NavLink to="/orders" className={tabClass} aria-label="Orders">
            <Icon name="clock" size={24} />
            <span className={styles.tabLabel}>Orders</span>
          </NavLink>
          <NavLink to="/favourites" className={tabClass} aria-label={favLabel}>
            <Icon name="heart" size={24} />
            <span className={styles.tabLabel}>Favourites</span>
          </NavLink>
          <button
            type="button"
            className={styles.tab}
            data-placeholder="offers"
            aria-label={`Offers, ${OFFERS_COUNT} (coming soon)`}
          >
            <Icon name="tag" size={24} />
            <span className={styles.tabLabel}>Offers</span>
          </button>
        </nav>
      )}
      {/* The host's cart side panel: a portalled dialog, so it renders once here
          whichever bar the cart button sits in. */}
      {cartPanel}
    </header>
  );
}

export default SiteHeader;
